import {Component, effect, inject, Input, OnInit, signal, Signal} from '@angular/core';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {
  _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS,
  IAutoOrderAccountSettings,
  IOpenOrderSettings,
  SettingsReferenceEnum,
} from '../../models';
import {selectSelectedSetting, selectSelectedStoreOpenOrderSettings} from '../../store/setting.selectors';
import {
  ICrudOptions,
  IUserAccessFeatureAutoOrderingSettings,
} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureAutoOrderingSettingsForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {settingsSetSelected, settingStoreAOStoreOrderScheduler} from '../../store/setting.actions';
import {getFieldErrorString} from '../../../../shared/utils/form/form.utils';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {logConsoleInfo} from '../../../../shared/shared-module-ui/utils/logger/logger';

@Component({
  selector: 'app-feature-auto-order-scheduler-settings',
  standalone: false,
  templateUrl: './feature-auto-order-scheduler-settings.component.html',
  styleUrl: './feature-auto-order-scheduler-settings.component.scss',
})
export class FeatureAutoOrderSchedulerSettingsComponent implements OnInit {

  //#region INPUTS
  @Input() hideActionBar: boolean = false;
  //#endregion

  //#region PROPERTIES
  formAccountSettings: FormGroup;
  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected periodSelected = signal<string[]>([]);
  //#endregion

  //#region INJECTORS
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private formBuilder = inject(FormBuilder);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _uaOrderingSettings: Signal<IUserAccessFeatureAutoOrderingSettings> = this._store.selectSignal(selectUserAccessFeatureAutoOrderingSettingsForSelectedStore);
  private readonly _schedulerSettings: Signal<IOpenOrderSettings> = this._store.selectSignal(selectSelectedStoreOpenOrderSettings);
  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const access = this._uaOrderingSettings();
      this.initializeForms(access);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
    effect(() => {
      const schedulerSettings = this._schedulerSettings();
      if (schedulerSettings?.orderAccountSettings && this.formAccountSettings) {
        this.updateFormFromStore(schedulerSettings.orderAccountSettings);
      }
    });
  }

  //#endregion

  //#region ACCESSOR - USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get orderingSettingsUserAccess(): IUserAccessFeatureAutoOrderingSettings {
    return this._uaOrderingSettings();
  }

  //#endregion

  //#region ACCESSOR - ACCOUNT SETTINGS

  get hasAccountSettingsBeenEdited(): boolean {
    return this.formAccountSettings?.dirty ?? false;
  }

  get hasAccountSettingsErrors(): { errors: ValidationErrors; error: string; field: string }[] {
    const errors: { errors: ValidationErrors; error: string; field: string }[] = [];
    const growthErrors = this.formAccountSettings.controls['growth']?.errors;
    const monthsHistoryErrors = this.formAccountSettings.controls['monthsHistory']?.errors;
    const periodErrors = this.formAccountSettings.controls['period']?.errors;
    const stockDaysErrors = this.formAccountSettings.controls['stockDays']?.errors;

    if (growthErrors) {
      errors.push({
        errors: growthErrors,
        error: getFieldErrorString(growthErrors, 'growth'),
        field: 'growth',
      });
    }
    if (monthsHistoryErrors) {
      errors.push({
        errors: monthsHistoryErrors,
        error: getFieldErrorString(monthsHistoryErrors, 'monthsHistory'),
        field: 'monthsHistory',
      });
    }
    if (periodErrors) {
      errors.push({
        errors: periodErrors,
        error: getFieldErrorString(periodErrors, 'period'),
        field: 'period',
      });
    }
    if (stockDaysErrors) {
      errors.push({
        errors: stockDaysErrors,
        error: getFieldErrorString(stockDaysErrors, 'stockDays'),
        field: 'stockDays',
      });
    }
    return errors;
  }

  get isAccountSettingsSubmitDisabled(): boolean {
    return (this.canAccountSettingsBeEdited === false) || (this.canAccountSettingsBeEdited === true && this.hasAccountSettingsBeenEdited === false) || (this.formAccountSettings?.invalid ?? true);
  }

  get canAccountSettingsBeEdited(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canEdit ?? false;
  }

  get canAccountSettingsBeSubmitted(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canSubmit ?? false;
  }

  get canAccountSettingsBeViewed(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return this.hasAccountSettingsBeenEdited;
  }

  //#endregion

  //#region Life Cycle Hooks
  ngOnInit(): void {
    this.formAccountSettings = this.formBuilder.group({});
    this._store.dispatch(settingStoreAOStoreOrderScheduler.getAOOrderScheduler({
      params: {storeAction: 'Selected'},
    }));
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
      case 'accountSettings':
        this.revertAccountSettings();
        break;
      default:
        break;
    }
  }

  onSubmitForm(form: string): void {
    switch (form) {
      case 'all':
      case 'accountSettings':
        if (!this.canAccountSettingsBeSubmitted) break;
        const currentSchedulerSettings = this._schedulerSettings();
        const updatedAccountSettings: IAutoOrderAccountSettings = {
          ...currentSchedulerSettings?.orderAccountSettings,
          stockDays: this.formAccountSettings.value.stockDays,
          growth: this.formAccountSettings.value.growth,
          monthsHistory: this.formAccountSettings.value.monthsHistory,
          period: this.formAccountSettings.value.period,
        };
        this._store.dispatch(settingStoreAOStoreOrderScheduler.setAOOrderScheduler({
          settings: updatedAccountSettings,
        }));
        this.formAccountSettings.markAsPristine();
        break;
      default:
        break;
    }
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  //#region FORM - REVERT FORM EDITS
  revertAccountSettings(): void {
    const schedulerSettings = this._schedulerSettings();
    const accountSettings = schedulerSettings?.orderAccountSettings ?? _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS;
    this.formAccountSettings.reset({
      stockDays: accountSettings?.stockDays ?? 30,
      growth: accountSettings?.growth ?? 10,
      monthsHistory: accountSettings?.monthsHistory ?? 3,
      period: accountSettings?.period ?? 2,
    });
  }

  private updateFormFromStore(accountSettings: IAutoOrderAccountSettings): void {
    if (this.formAccountSettings && this.formAccountSettings.pristine) {
      this.formAccountSettings.patchValue({
        stockDays: accountSettings?.stockDays ?? 30,
        growth: accountSettings?.growth ?? 10,
        monthsHistory: accountSettings?.monthsHistory ?? 3,
        period: accountSettings?.period ?? 2,
      }, {emitEvent: false});
      this.formAccountSettings.markAsPristine();
    }
  }

  private initializeForms(access: IUserAccessFeatureAutoOrderingSettings): void {
    this.buildAccountSettingsForm(access?.openOrderSettings);
  }

  private buildAccountSettingsForm(access: ICrudOptions): void {
    if (access?.canView && this.formAccountSettings.pristine) {
      const schedulerSettings = this._schedulerSettings();
      const accountSettings = schedulerSettings?.orderAccountSettings ?? {..._DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS};
      const {growth, monthsHistory, period, stockDays} = accountSettings;
      const tempForm = this.formBuilder.group({});

      this.addFormControl(tempForm, 'stockDays', stockDays ?? 30, false, [
        Validators.required,
        Validators.min(0),
        Validators.max(255),
      ]);
      this.addFormControl(tempForm, 'growth', growth ?? 10, false, [
        Validators.required,
        Validators.min(-255),
        Validators.max(255),
      ]);
      this.addFormControl(tempForm, 'monthsHistory', monthsHistory ?? 3, false, [
        Validators.required,
        Validators.min(1),
        Validators.max(12),
      ]);
      this.addFormControl(tempForm, 'period', period ?? 2, false, [
        Validators.required,
        Validators.min(1),
        Validators.max(3),
      ]);

      tempForm.markAsPristine();
      this.formAccountSettings = tempForm;
    }
  }

  //#endregion

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  //#endregion

  //#region FORM - ON CLICK
  onPeriodChange(selected: { [key: string]: ISelectOption }): void {
    const selection = Object.keys(selected).filter((option: string) => selected[option].selected);
    const selectedKey: string = selection?.length > 0 ? selection[0] : 'period-2';
    const periodValue = parseInt(selectedKey.replace('period-', ''), 10);
    this.formAccountSettings.controls['period'].setValue(periodValue);
    this.formAccountSettings.markAsDirty();
  }

  //#endregion

  //#region SELECT - OPTIONS
  getPeriodOptions(): { [key: string]: ISelectOption } {
    const currentPeriod = this.formAccountSettings?.controls['period']?.value;
    return {
      ['period-1']: {
        name: 'Seasonal',
        selected: currentPeriod === 1,
        disabled: false,
      },
      ['period-2']: {
        name: 'Max',
        selected: currentPeriod === 2,
        disabled: false,
      },
      ['period-3']: {
        name: 'Last X Months',
        selected: currentPeriod === 3,
        disabled: false,
      },
    };
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.featureAutoOrderOpenOrderSettings}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

//#endregion

}